import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  FileText,
  Scale,
  Briefcase,
  AlertCircle,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { mockActivity, activityTypeConfig, type ActivityItem } from './mockData';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Scale,
  Briefcase,
  AlertCircle,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 400, damping: 30 },
  },
};

function ActivityItemRow({ activity, isLast }: { activity: ActivityItem; isLast: boolean }) {
  const navigate = useNavigate();
  const config = activityTypeConfig[activity.type];
  const Icon = iconMap[config.icon] || FileText;

  const timeAgo = formatDistanceToNow(activity.timestamp, {
    addSuffix: false,
    locale: de,
  });

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ x: 2 }}
      onClick={() => {
        const caseId = activity.caseRef.split('-').pop();
        navigate({ to: `/lawyer/cases/${caseId}` });
      }}
      className="flex gap-2 cursor-pointer group"
    >
      {/* Timeline indicator */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center shrink-0',
            'transition-transform group-hover:scale-110',
            config.bgColor
          )}
        >
          <Icon className={cn('h-3 w-3', config.textColor)} />
        </div>
        {!isLast && (
          <div className="w-0.5 h-full bg-slate-100 my-0.5" />
        )}
      </div>

      {/* Content */}
      <div className={cn('flex-1 pb-2 min-w-0')}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-700 group-hover:text-slate-900 transition-colors truncate">
            {activity.description}
            <span className="text-slate-400 ml-1 font-mono">{activity.caseRef}</span>
          </p>
          <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">
            {timeAgo}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function ActivityTimeline() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.25 }}
      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden h-full flex flex-col min-w-0"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-600" />
          <h2 className="font-semibold text-slate-900 text-sm">Letzte Aktivität</h2>
        </div>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
          {mockActivity.length}
        </span>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-3">
        {mockActivity.length === 0 ? (
          <div className="text-center py-6">
            <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Keine aktuellen Aktivitäten</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mockActivity.map((activity, index) => (
              <ActivityItemRow
                key={activity.id}
                activity={activity}
                isLast={index === mockActivity.length - 1}
              />
            ))}
          </motion.div>
        )}
      </div>

      {/* Footer */}
      {mockActivity.length > 0 && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-50">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs text-slate-600 hover:text-slate-900"
            onClick={() => {
              // TODO: Navigate to full activity log
            }}
          >
            Alle Aktivitäten anzeigen
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      )}
    </motion.div>
  );
}
